import type { FC } from 'react';
import { useNavigate } from 'react-router';

import { ButtonRow } from '../../components/global/ButtonRow/ButtonRow';
import { FlowStepWithContainer } from '../../components/layout/FlowStepWithContainer';
import { usePromoterItemQuery } from '../../data/promoter/usePromoterItemQuery.ts';
import { usePromoterItemUpdateMutation } from '../../data/promoter/usePromoterItemUpdateMutation.ts';
import { useUserQuery } from '../../data/user/useUserQuery.ts';
import { useUserUpdateMutation } from '../../data/user/useUserUpdateMutation.ts';
import { UserAndPromoterForm } from '../../forms/UserAndPromoter/UserAndPromoterForm.tsx';
import { getCurrentStepInfo, getNextStep } from './eventOnboardingFlow.ts';

export const UserAndPromoter: FC = () => {
  const navigate = useNavigate();

  const { data: userData } = useUserQuery();
  const { data: promoterData } = usePromoterItemQuery();

  const { mutate: mutatePromoter, isPending: mutatePromoterIsPending } = usePromoterItemUpdateMutation();
  const { mutate: mutateUser, isPending: mutateUserIsPending } = useUserUpdateMutation();

  const currentStep = 'user-and-promoter';
  const { currentStep: stepNumber, totalSteps, label: heading } = getCurrentStepInfo(currentStep);

  const onSubmit = ({
    firstName,
    lastName,
    organisationName,
    phoneNumber,
  }: {
    firstName: string;
    lastName: string;
    organisationName: string;
    phoneNumber: string;
  }) => {
    mutateUser(
      { first_name: firstName, last_name: lastName },
      {
        onSuccess: () => {
          mutatePromoter(
            { name: organisationName, phone_number: phoneNumber },
            {
              onSuccess: () => {
                void navigate(`../${getNextStep(currentStep)}`);
              },
            },
          );
        },
      },
    );
  };

  const data =
    promoterData && userData
      ? {
          organisationName: promoterData.name ?? '',
          phoneNumber: promoterData.phone_number ?? '',
          firstName: userData.first_name ?? '',
          lastName: userData.last_name ?? '',
        }
      : undefined;

  if (!data) {
    return null;
  }

  return (
    <FlowStepWithContainer
      bodyContent={
        <UserAndPromoterForm
          buttonSlot={
            <ButtonRow
              primaryButton={{
                type: 'submit',
                text: 'Next',
                isPending: mutatePromoterIsPending || mutateUserIsPending,
              }}
            />
          }
          defaultValues={data}
          onSubmit={onSubmit}
        />
      }
      heading={heading}
      progress={{ currentStep: stepNumber, totalSteps }}
    />
  );
};
